import { Card, CardBody, CardHeader, type ColumnDef, InlineMessage, RelatedList, Spinner, Stack } from '@olytic/foundation';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { formatDate, formatMoney, humanize } from '../objects/format';
import { recordHref, relatedPath } from '../objects/routes';
import { MonthGrid } from './MonthGrid';
import { fetchRelated, type ProjectRelated, type RelatedMeeting, type RelatedOpportunity, type WorkProject } from './workApi';

const OPPORTUNITY_COLUMNS: ColumnDef<RelatedOpportunity>[] = [
  { key: 'name', label: 'Opportunity', primary: true },
  { key: 'stage', label: 'Stage', render: (r) => humanize(r.stage) },
  { key: 'amount', label: 'Amount', align: 'right', render: (r) => formatMoney(r.amount) },
  { key: 'close_date', label: 'Close Date', render: (r) => formatDate(r.close_date) },
];

const MEETING_COLUMNS: ColumnDef<RelatedMeeting>[] = [
  { key: 'name', label: 'Meeting Name', primary: true },
  { key: 'meeting_date', label: 'Date', render: (r) => formatDate(r.meeting_date) },
  { key: 'summary_line', label: 'Summary', render: (r) => r.summary_line ?? '—' },
];

/** A project's opportunity, meetings and forward resourcing. */
export function WorkRelated({ project }: { project: WorkProject }): JSX.Element {
  const navigate = useNavigate();
  const [data, setData] = useState<ProjectRelated | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    fetchRelated(authenticatedFetch, project.id)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load the related records.'));
    return () => {
      live = false;
    };
  }, [project.id]);

  if (error) return <div className="pt-4"><InlineMessage tone="error" role="alert">{error}</InlineMessage></div>;
  if (!data) return <div className="flex justify-center py-10"><Spinner label="Loading related records" /></div>;

  const links = { recordHref, onNavigate: (href: string) => navigate(href) };
  const named = new Map<string, string>();
  for (const a of data.assignments) named.set(a.app_user_id ?? '', a.app_user_id ? a.name ?? 'Someone' : 'Unfilled');
  const people = [...named].map(([key, label]) => ({ key, label }))
    .sort((a, b) => Number(a.key === '') - Number(b.key === '') || a.label.localeCompare(b.label));
  const months = [...new Set(data.assignments.map((a) => a.month))].sort();
  const cell = new Map(data.assignments.map((a) => [`${a.month}|${a.app_user_id ?? ''}`, a.hours]));

  return (
    <Stack gap="lg" className="pt-4">
      {data.opportunities ? (
        <RelatedList<RelatedOpportunity>
          title="Opportunities"
          object="opportunities"
          columns={OPPORTUNITY_COLUMNS}
          rows={data.opportunities}
          getRowId={(r) => r.id}
          compactEmpty
          emptyLabel="No opportunity on this project."
          {...links}
        />
      ) : null}
      {data.meetings ? (
        <RelatedList<RelatedMeeting>
          title="Meetings"
          object="meetings"
          columns={MEETING_COLUMNS}
          rows={data.meetings}
          getRowId={(r) => r.id}
          previewCount={10}
          {...(data.meetings.length > 10 ? { viewAllHref: relatedPath('projects', project.id, 'meetings') } : {})}
          compactEmpty
          emptyLabel="No meetings on this project."
          {...links}
        />
      ) : null}
      <Card>
        <CardHeader title="Resourcing" />
        <CardBody>
          <MonthGrid
            caption={`Assigned hours on ${project.code} by month and person, this month on`}
            months={months}
            people={people}
            hours={(month, person) => cell.get(`${month}|${person}`) ?? 0}
            empty="No hours assigned from this month on."
          />
        </CardBody>
      </Card>
    </Stack>
  );
}
